import { QuestionNotification } from '@agilelab/plugin-wb-notification-common';
import {
  customAlertApiRef,
  ProgressButton,
  useResponsiveMobile,
  useUsername,
  useUserProfile,
  WbAvatar,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import { Entity } from '@mainstage/catalog-model';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import QuestionAnswerOutlinedIcon from '@material-ui/icons/QuestionAnswerOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useAsync from 'react-use/lib/useAsync';
import { JsonObject } from 'type-fest';
import { notificationApiRef } from '../../notificationApiRef';
import { userCache } from './cache';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureAction,
  NotificationDisclosureDetails,
  NotificationDisclosureSummary,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import { useNotification } from './useNotificationProvider';

const AnswerButton = (props: { notification: QuestionNotification }) => {
  const { notification } = props;
  const notificationCatalog = useApi(notificationApiRef);
  const identityApi = useApi(identityApiRef);
  const alertApi = useApi(customAlertApiRef);
  const queryClient = useQueryClient();
  const isMobile = useResponsiveMobile();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { profile } = useUserProfile();
  const loggedUsername = useUsername();
  const { setRead } = useNotification();

  const { control, handleSubmit, reset } = useForm<{ answer: string }>({
    mode: 'onChange',
  });

  const { mutate: answerQuestion, isLoading } = useMutation({
    mutationFn: async (response: { id: string; body: any }) =>
      notificationCatalog.updateNotificationResponse(
        response.id,
        response.body,
        await identityApi.getCredentials(),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paginatedByRecipient'] });
      alertApi.post({
        message: `Answer sent successfully!`,
        severity: 'success',
        timeoutMillis: 5000,
      });
    },
    onError: (error: Error) => {
      alertApi.post({
        error: new CustomError('Failed to send answer', error.message),
        severity: 'error',
        timeoutMillis: 5000,
      });
    },
  });

  const handleResult = useCallback(
    ({ answer }: { answer: string }) => {
      answerQuestion({
        id: notification.id.toString(),
        body: {
          answer: answer,
          answered_by: loggedUsername?.username || profile.email,
          answered_by_display_name:
            loggedUsername?.displayName || profile.displayName,
          timestamp: new Date().toJSON(),
          data_product_urn: notification.notification_request.dataproduct.urn,
        },
      });
      reset();
      setIsOpen(false);
      setRead(notification.id.toString());
    },
    [
      answerQuestion,
      loggedUsername?.displayName,
      loggedUsername?.username,
      notification.id,
      notification.notification_request.dataproduct.urn,
      profile.displayName,
      profile.email,
      reset,
      setRead,
    ],
  );

  return (
    <>
      <Dialog fullScreen={isMobile} open={isOpen} fullWidth maxWidth="sm">
        <DialogTitle>Answer the question</DialogTitle>
        <form
          onSubmit={handleSubmit(handleResult)}
          autoComplete="off"
          noValidate
        >
          <DialogContent>
            <Typography
              variant="body1"
              style={{ marginBottom: theme.spacing(1) }}
            >
              <b>{notification.notification_request.asked_by_display_name}</b>{' '}
              asked a question about the Data Product:
            </Typography>
            <Typography variant="body1" color="primary">
              <b>
                {notification.notification_request.dataproduct.display_name ||
                  notification.notification_request.dataproduct.name}
              </b>{' '}
              v<b>{notification.notification_request.dataproduct.version}</b>
            </Typography>
            <Typography variant="body1" style={{ margin: theme.spacing(2, 0) }}>
              <b>Question:</b> {notification.notification_request.question}
            </Typography>

            <Controller
              control={control}
              name="answer"
              rules={{ required: 'Answer required' }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Answer the question"
                  id="answer"
                  style={{ width: '100%' }}
                  variant="filled"
                  multiline
                  rows={6}
                  error={Boolean(error)}
                  helperText={error?.message || ' '}
                  {...field}
                />
              )}
            />
          </DialogContent>
          <DialogActions
            style={{ paddingRight: '1.5rem', paddingBottom: '1rem' }}
          >
            <Button
              color="secondary"
              variant="text"
              onClick={() => {
                reset();
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <ProgressButton
              type="submit"
              color="primary"
              variant="contained"
              inProgress={isLoading}
            >
              Send
            </ProgressButton>
          </DialogActions>
        </form>
      </Dialog>
      <Button
        onClick={() => setIsOpen(true)}
        color="primary"
        variant="contained"
      >
        Answer the question
      </Button>
    </>
  );
};

export const QuestionNotificationDisclosure = (props: {
  notification: QuestionNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const catalogApi = useApi(catalogApiRef);
  const theme = useTheme();

  const { value: askerEntity } = useAsync(async (): Promise<
    Entity | undefined
  > => {
    if (userCache.has(notification.notification_request.asked_by))
      return userCache.get(notification.notification_request.asked_by)!;
    const userEntity = await catalogApi.getEntityByRef({
      name: notification.notification_request.asked_by,
      namespace: 'default',
      kind: 'user',
    });
    if (userEntity) {
      userCache.set(notification.notification_request.asked_by, userEntity);
    }
    return userEntity;
  }, [notification]);

  const onToggle = useCallback(
    (_event: object, expanded: boolean) => {
      onAccordionExpansionsChange(
        expanded ? notification.id.toString() : false,
      );
    },
    [notification.id, onAccordionExpansionsChange],
  );

  return (
    <NotificationDisclosure
      expanded={expandedAccordion === notification.id.toString()}
      onChange={onToggle}
    >
      <NotificationDisclosureSummary expandIcon={<ExpandMoreIcon />}>
        <div className={classes.summary}>
          <Box>
            <div className={classes.summaryHeader}>
              <Typography className={classes.notificationTime}>
                {DateTime.fromISO(notification.created_at).toFormat(
                  'dd/MM/yyyy HH:mm',
                )}
              </Typography>
              <QuestionAnswerOutlinedIcon fontSize="small" color="primary" />
            </div>
            <div className={classes.summaryDescription}>
              <WbAvatar
                displayName={
                  notification.notification_request.asked_by_display_name
                }
                picture={
                  (askerEntity?.spec?.profile as JsonObject | undefined)
                    ?.picture as string
                }
                classes={{ avatar: classes.avatar }}
              />
              <div className={classes.summaryDescriptionText}>
                <b>{notification.notification_request.asked_by_display_name}</b>
                <Typography variant="subtitle2">
                  asked a question about the data product
                </Typography>
                <span>
                  <b>name:</b>
                  <WbLink
                    to={`/marketplace/search/${notification.notification_request.id_dataproduct_instance}`}
                  >
                    <Typography color="primary" component="b">
                      {notification.notification_request.dataproduct
                        .display_name ||
                        notification.notification_request.dataproduct.name}
                    </Typography>
                  </WbLink>
                </span>
                <span>
                  <b>domain:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.domain}
                  </Typography>
                </span>
                <span>
                  <b>environment:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.environment}
                  </Typography>
                </span>
                <span>
                  <b>version:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.version}
                  </Typography>
                </span>
              </div>
            </div>
          </Box>
          <Box alignSelf="center">
            <MarkAsReadButton notification={notification} />
          </Box>
        </div>
      </NotificationDisclosureSummary>
      <NotificationDisclosureDetails>
        <div>
          <Typography variant="body1">
            <b>Question: </b>
            {notification.notification_request.question}
          </Typography>
          {notification.notification_response?.answer && (
            <Typography variant="body1" style={{ marginTop: theme.spacing(2) }}>
              <b>Answer: </b>
              {notification.notification_response.answer}
            </Typography>
          )}
        </div>
      </NotificationDisclosureDetails>
      <NotificationDisclosureAction>
        {!notification.notification_response?.answer && (
          <AnswerButton notification={notification} />
        )}
      </NotificationDisclosureAction>
    </NotificationDisclosure>
  );
};
